import { Module } from 'vuex';
import { AxiosResponse } from 'axios';
import {
  SET_ACCOUNT,
  SET_ACCOUNT_ORGANIZATION,
  SET_SESSION_TOKEN,
  SET_ERRORMSG,
} from '../mutation_types';
import {
  LOGIN,
  SIGNUP,
  UPDATE_ACCOUNT,
  GET_AUTH_ACCOUNT,
  SYNC_ACCOUNT_ORGANIZATION,
  CLEAR_ACCOUNT_ISLOADING,
  LOGOUT,
  SIGN_OUT,
  RESET_PASSWORD_AUTHED,
  FORGOT_PASSWORD,
  RESET_PASSWORD,
} from '../action_types';
import { StateObject } from '@/dtos/state.dto';
import { LoginCredentals, SignupCredentals } from '@/dtos/auth.dto';
import { LOCAL_STORAGE_KEY } from '@/utils/const';
import {
  EditEmployee,
  ForgotPassword,
  GetEmployee,
  ResetPassword,
  ResetPasswordObj,
} from '@/dtos/employee.dto';
import { GetOrganization } from '@/dtos/organization.dto';
import Auth from '@/services/auth';
import Employee from '@/services/employee';
import Organization from '@/services/organization';

/**
 * The organization (and with it the enabled modules) is refreshed in the background
 * as the user moves around, at most once per interval unless a caller forces it.
 * Kept outside the persisted state so every page load syncs once.
 */
const ORGANIZATION_SYNC_INTERVAL_MS = 60 * 1000;
let organizationSyncedAt = 0;
let organizationSync: Promise<boolean> | null = null;

const account: Module<any, {}> = {
  state: {
    account: {
      data: null,
      status: null,
      isLoading: false,
    },
    sessionToken: null,
  },
  getters: {
    account: (state: { account: any }) => state.account,
    sessionToken: (state: { sessionToken: any }) => state.sessionToken,
  },
  mutations: {
    [SET_ACCOUNT]: (
      state: { account: StateObject },
      payload: { data: null; status: any; isLoading: any }
    ) => {
      payload.data != null ? (state.account.data = payload.data) : null,
        (state.account.status = payload.status),
        (state.account.isLoading = payload.isLoading);
    },
    [SET_SESSION_TOKEN]: (state: { sessionToken: any }, payload: any) => {
      state.sessionToken = payload;
    },
    [SET_ACCOUNT_ORGANIZATION]: (state: { account: StateObject }, payload: any) => {
      if (state.account.data) {
        state.account.data = { ...state.account.data, organization: payload };
      }
    },
  },
  actions: {
    [LOGIN]: async ({ commit }: any, payload: any) => {
      commit(SET_ACCOUNT, { data: null, isLoading: true });

      const result: AxiosResponse = await Auth.login(
        payload as LoginCredentals
      );

      if (result) {
        if (result.data.status) {
          console.log(result.data);
          commit(SET_SESSION_TOKEN, result.data.sessionToken);
          commit(SET_ACCOUNT, { data: result.data.data, isLoading: false });
          return true;
        }
        commit(SET_ACCOUNT, { data: null, isLoading: false });
        commit(SET_ERRORMSG, result.data.msg);
        return false;
      } else {
        commit(SET_ACCOUNT, { isLoading: false });
        return false;
      }
    },
    [SIGNUP]: async ({ commit }: any, payload: any) => {
      commit(SET_ACCOUNT, { data: null, isLoading: true });

      const result: AxiosResponse = await Auth.signup(
        payload as SignupCredentals
      );

      if (result) {
        if (result.data.status) {
          commit(SET_SESSION_TOKEN, result.data.sessionToken);
          commit(SET_ACCOUNT, { data: result.data.data, isLoading: false });
          return true;
        }
        commit(SET_ACCOUNT, { data: null, isLoading: false });
        commit(SET_ERRORMSG, result.data.msg);
        return false;
      } else {
        commit(SET_ACCOUNT, { isLoading: false });
        return false;
      }
    },
    [UPDATE_ACCOUNT]: async ({ commit }: any, payload: any) => {
      commit(SET_ACCOUNT, { isLoading: true });

      const result: AxiosResponse = await Auth.updateAccount(
        payload as EditEmployee
      );

      if (result) {
        if (result.data.status) {
          commit(SET_ACCOUNT, { data: result.data.data, isLoading: false });
          return true;
        }
        commit(SET_ERRORMSG, result.data.msg);
        return false;
      } else {
        commit(SET_ACCOUNT, { isLoading: false });
        return false;
      }
    },
    [FORGOT_PASSWORD]: async ({ commit }: any, payload: any) => {
      commit(SET_ACCOUNT, { isLoading: true });

      const result: AxiosResponse = await Auth.forgotPassword(
        payload as ForgotPassword
      );

      if (result) {
        if (result.data.status) {
          commit(SET_ACCOUNT, { isLoading: false });
          return true;
        }
        commit(SET_ERRORMSG, result.data.msg);
        return false;
      } else {
        commit(SET_ACCOUNT, { isLoading: false });
        return false;
      }
    },
    [RESET_PASSWORD]: async ({ commit }: any, payload: any) => {
      commit(SET_ACCOUNT, { isLoading: true });

      const result: AxiosResponse = await Auth.resetPassword(
        payload as ResetPasswordObj
      );

      if (result) {
        if (result.data.status) {
          commit(SET_ACCOUNT, { isLoading: false });
          return true;
        }
        commit(SET_ERRORMSG, result.data.msg);
        return false;
      } else {
        commit(SET_ACCOUNT, { isLoading: false });
        return false;
      }
    },
    [RESET_PASSWORD_AUTHED]: async ({ commit }: any, payload: any) => {
      commit(SET_ACCOUNT, { isLoading: true });

      const result: AxiosResponse = await Auth.resetPasswordAuthed(
        payload as ResetPassword
      );

      if (result) {
        if (result.data.status) {
          commit(SET_ACCOUNT, { isLoading: false });
          return true;
        }
        commit(SET_ERRORMSG, result.data.msg);
        return false;
      } else {
        commit(SET_ACCOUNT, { isLoading: false });
        return false;
      }
    },
    [GET_AUTH_ACCOUNT]: async ({ commit, state }: any) => {
      commit(SET_ACCOUNT, { isLoading: true });

      const result: AxiosResponse = await Employee.getEmployees({
        id: state.account.data.id,
      } as GetEmployee);

      if (result) {
        if (result.data.status) {
          commit(SET_ACCOUNT, { data: result.data.data[0], isLoading: false });
          return true;
        }
        commit(SET_ERRORMSG, result.data.msg);
        return false;
      } else {
        commit(SET_ACCOUNT, { isLoading: false });
        return false;
      }
    },
    [SYNC_ACCOUNT_ORGANIZATION]: ({ commit, state }: any, payload?: { force?: boolean }) => {
      const account = state.account.data;
      const organizationId = account?.organizationId ?? account?.organization?.id;
      if (!organizationId || !state.sessionToken) return Promise.resolve(false);

      if (organizationSync) return organizationSync;
      if (!payload?.force && Date.now() - organizationSyncedAt < ORGANIZATION_SYNC_INTERVAL_MS) return Promise.resolve(false);

      organizationSync = Organization.getOrganizations({ id: organizationId } as GetOrganization)
        .then((result: AxiosResponse | undefined) => {
          const organization = result?.data?.status && Array.isArray(result.data.data) ? result.data.data[0] : null;
          if (!organization) return false;
          organizationSyncedAt = Date.now();
          commit(SET_ACCOUNT_ORGANIZATION, organization);
          return true;
        })
        .catch(() => false)
        .finally(() => {
          organizationSync = null;
        });

      return organizationSync;
    },
    [LOGOUT]: async ({ commit }) => {
      organizationSyncedAt = 0;
      commit(SET_SESSION_TOKEN, null);
      commit(SET_ACCOUNT, { data: null, isLoading: false });
      localStorage.removeItem(LOCAL_STORAGE_KEY);
      return true;
    },
    // The sign-out button: the activity log hears of it, then the session is forgotten. Ended
    // sessions (an expired token) go straight to LOGOUT, so this never loops on a 401.
    [SIGN_OUT]: async ({ dispatch }) => {
      try {
        await Auth.logout();
      } catch (error) {
        // signing out never waits on the API
      }
      return dispatch(LOGOUT);
    },
    [CLEAR_ACCOUNT_ISLOADING]: ({ commit }) => {
      commit(SET_ACCOUNT, { isLoading: false });
    },
  },
};

export default account;
